import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import type { RaspadinhaContemplatedPrizeImage } from '@/types/raspadinha-campaign'
import {
  generateRandomWinningTimeSlots,
  formatCampaignDateLabel,
  listSelectableContemplatedPrizes,
  MAX_AUTO_GENERATED_WINNERS,
  MIN_AUTO_GENERATED_WINNERS,
  type GeneratedWinningTimeSlotInput,
} from '@/lib/raspadinha-campaigns'
import layoutStyles from '@/layouts/CampanhasLayout/CampanhasLayout.module.css'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import styles from './AutoGenerateTimeSlotsModal.module.css'

export type AutoGenerateTimeSlotsFormValues = {
  winnerCount: number
  startTime: string
  endTime: string
}

type AutoGenerateTimeSlotsModalProps = {
  isOpen: boolean
  date: string | null
  dayNumber: number | null
  contemplatedPrizes: RaspadinhaContemplatedPrizeImage[]
  occupiedTimes: readonly string[]
  onClose: () => void
  onGenerate: (generatedSlots: GeneratedWinningTimeSlotInput[]) => void
}

const DEFAULT_FORM: AutoGenerateTimeSlotsFormValues = {
  winnerCount: 5,
  startTime: '09:00',
  endTime: '18:00',
}

function buildDefaultSelectedPrizeId(prizes: RaspadinhaContemplatedPrizeImage[]): string {
  return listSelectableContemplatedPrizes(prizes)[0]?.id ?? ''
}

export function AutoGenerateTimeSlotsModal({
  isOpen,
  date,
  dayNumber,
  contemplatedPrizes,
  occupiedTimes,
  onClose,
  onGenerate,
}: AutoGenerateTimeSlotsModalProps) {
  const [form, setForm] = useState<AutoGenerateTimeSlotsFormValues>(DEFAULT_FORM)
  const [selectedPrizeId, setSelectedPrizeId] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const selectablePrizes = useMemo(
    () => listSelectableContemplatedPrizes(contemplatedPrizes),
    [contemplatedPrizes]
  )

  useEffect(() => {
    if (!isOpen) return

    setForm(DEFAULT_FORM)
    setSelectedPrizeId(buildDefaultSelectedPrizeId(contemplatedPrizes))
    setErrorMessage(null)
  }, [contemplatedPrizes, isOpen, date])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !date || typeof document === 'undefined') return null

  const dateLabel = formatCampaignDateLabel(date, dayNumber ?? undefined)

  const handleGenerate = () => {
    setErrorMessage(null)

    if (!form.startTime || !form.endTime) {
      setErrorMessage('Informe o horário de início e o horário final.')
      return
    }

    if (selectablePrizes.length === 0) {
      setErrorMessage('Configure ao menos um prêmio em Mídia e prêmios antes de gerar horários.')
      return
    }

    if (!selectedPrizeId) {
      setErrorMessage('Selecione o prêmio que será contemplado nos horários gerados.')
      return
    }

    const result = generateRandomWinningTimeSlots({
      winnerCount: form.winnerCount,
      startTime: form.startTime,
      endTime: form.endTime,
      occupiedTimes,
      contemplatedPrizeId: selectedPrizeId,
    })

    if (!result.ok) {
      setErrorMessage(result.error)
      return
    }

    onGenerate(result.slots)
    onClose()
  }

  return createPortal(
    <div
      className={layoutStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auto-generate-time-slots-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className={layoutStyles.modalCard}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={layoutStyles.modalHeader}>
          <div>
            <h2 id="auto-generate-time-slots-modal-title" className={layoutStyles.modalTitle}>
              Gerar horários automaticamente
            </h2>
            <p className={layoutStyles.modalSubtitle}>
              Defina quantos ganhadores haverá em {dateLabel}, o período do dia e o prêmio que será
              contemplado. Os horários serão sorteados de forma aleatória dentro do intervalo
              informado. Depois, revise a lista e clique em <strong>Salvar Horários</strong> para
              confirmar.
            </p>
          </div>
          <button
            type="button"
            className={layoutStyles.modalClose}
            onClick={onClose}
            aria-label="Fechar geração automática de horários"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>

        <div className={layoutStyles.modalForm}>
          <div className={`${layoutStyles.modalBody} ${styles.modalBody}`}>
            <div className={styles.formSections}>
            <div className={editStyles.editField}>
              <label className={editStyles.editLabel} htmlFor="auto-generate-winner-count">
                Quantidade de ganhadores no dia
              </label>
              <input
                id="auto-generate-winner-count"
                type="number"
                className={editStyles.editInput}
                min={MIN_AUTO_GENERATED_WINNERS}
                max={MAX_AUTO_GENERATED_WINNERS}
                step={1}
                value={form.winnerCount}
                onChange={(event) => {
                  setErrorMessage(null)
                  setForm((previous) => ({
                    ...previous,
                    winnerCount: Number(event.target.value),
                  }))
                }}
                required
                autoFocus
              />
              <p className={editStyles.editLabelHint}>
                Será criado um horário de contemplação para cada ganhador ({MIN_AUTO_GENERATED_WINNERS}{' '}
                a {MAX_AUTO_GENERATED_WINNERS}).
              </p>
            </div>

            <div className={styles.prizeField}>
              <label className={editStyles.editLabel} id="auto-generate-prize-label">
                Prêmio
              </label>
              <p className={editStyles.editLabelHint}>
                Escolha um prêmio de Mídia e prêmios. Todos os horários gerados contemplarão este
                prêmio.
              </p>

              {selectablePrizes.length === 0 ? (
                <p className={styles.prizeEmptyState}>
                  Nenhum prêmio disponível. Cadastre ao menos um prêmio com título em{' '}
                  <strong>Mídia e prêmios</strong> antes de gerar horários.
                </p>
              ) : (
                <ul
                  className={styles.prizeList}
                  role="listbox"
                  aria-labelledby="auto-generate-prize-label"
                >
                  {selectablePrizes.map((prize) => {
                    const isSelected = selectedPrizeId === prize.id

                    return (
                      <li key={prize.id} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={`${styles.prizeOption} ${isSelected ? styles.prizeOptionSelected : ''}`}
                          onClick={() => {
                            setErrorMessage(null)
                            setSelectedPrizeId(prize.id)
                          }}
                        >
                          <input
                            type="radio"
                            className={styles.prizeRadio}
                            name="auto-generate-prize"
                            value={prize.id}
                            checked={isSelected}
                            readOnly
                            tabIndex={-1}
                            aria-hidden
                          />
                          {prize.image_url.trim() ? (
                            <img src={prize.image_url} alt="" className={styles.prizeThumb} />
                          ) : (
                            <span className={styles.prizeThumbPlaceholder} aria-hidden>
                              <Icon icon="mdi:gift-outline" />
                            </span>
                          )}
                          <span className={styles.prizeInfo}>
                            <span className={styles.prizeTitle}>{prize.title}</span>
                            <span className={styles.prizeMeta}>
                              Limite de contemplações: {prize.max_contemplations}
                            </span>
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            <div className={styles.timeFieldsSection}>
            <div className={editStyles.editGrid2}>
              <div className={editStyles.editField}>
                <label className={editStyles.editLabel} htmlFor="auto-generate-start-time">
                  Horário de início
                </label>
                <input
                  id="auto-generate-start-time"
                  type="time"
                  className={editStyles.editInput}
                  value={form.startTime}
                  onChange={(event) => {
                    setErrorMessage(null)
                    setForm((previous) => ({ ...previous, startTime: event.target.value }))
                  }}
                  required
                />
              </div>
              <div className={editStyles.editField}>
                <label className={editStyles.editLabel} htmlFor="auto-generate-end-time">
                  Horário final
                </label>
                <input
                  id="auto-generate-end-time"
                  type="time"
                  className={editStyles.editInput}
                  value={form.endTime}
                  onChange={(event) => {
                    setErrorMessage(null)
                    setForm((previous) => ({ ...previous, endTime: event.target.value }))
                  }}
                  required
                />
              </div>
            </div>

            {occupiedTimes.length > 0 ? (
              <p className={editStyles.editLabelHint}>
                {occupiedTimes.length} horário(s) já cadastrado(s) ou aguardando salvamento neste
                dia serão respeitados na geração.
              </p>
            ) : null}
            </div>

            {errorMessage ? (
              <div className={layoutStyles.modalError} role="alert">
                {errorMessage}
              </div>
            ) : null}
            </div>
          </div>

          <div className={layoutStyles.modalFooter}>
            <button
              type="button"
              className={layoutStyles.modalSecondaryButton}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={layoutStyles.modalPrimaryButton}
              onClick={handleGenerate}
            >
              <Icon icon="mdi:auto-fix" aria-hidden />
              Gerar horários
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
